import { useCallback, useEffect, useState } from 'react';
import {
  Alert as AntAlert,
  App,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, TagsOutlined } from '@ant-design/icons';
import {
  CORRELATION_ATTRIBUTE_PATTERN,
  MAX_CORRELATION_ATTRIBUTES,
  SIGNAL_TYPE_KEY_PATTERN,
  createSignalType,
  listSignalTypes,
  setSignalTypeEnabled,
  type SignalTypeRequest,
  type SignalTypeView,
} from '@/api/signalTypes';
import { normalizeApiError } from '@/api/errors';
import { formatRelative } from '@/lib/time';

const { Text, Paragraph } = Typography;

function Mono({ children }: { children: React.ReactNode }) {
  return (
    <Text className="ol-mono" style={{ fontSize: 11 }}>
      {children}
    </Text>
  );
}

function StatusTag({ status }: { status: string }) {
  switch (status) {
    case 'ACTIVE':
      return <Tag color="green">ACTIVE</Tag>;
    case 'RESERVED':
      return (
        <Tooltip title="Registered and documented, but the intake refuses it politely (409) until its coordination item lands. Not hub configuration.">
          <Tag color="gold">RESERVED</Tag>
        </Tooltip>
      );
    case 'DISABLED':
      return <Tag>DISABLED</Tag>;
    default:
      return <Tag>{status}</Tag>;
  }
}

export interface SignalTypesPanelProps {
  /** `alerts.rules.write` — reused: a signal type is as much an automation grant as a rule. */
  canWrite?: boolean;
}

interface FormShape {
  typeKey: string;
  title: string;
  description?: string;
  correlationAttributes?: string[];
}

/**
 * "Signal types" (OPSFL-FLOWOPS-1 Track B / B1) — the hub-authorable signal catalogue, the
 * panel that makes the machine rail above a real integration door: register a business event
 * type here, have the sending system HMAC-post it, and wire it to a flow under the flow's
 * **Triggers**. Platform-reserved rows are shown read-only (their status is a contract fact);
 * hub-authored rows can be disabled and re-enabled — never deleted.
 */
export function SignalTypesPanel({ canWrite = false }: SignalTypesPanelProps) {
  const { message } = App.useApp();
  const [types, setTypes] = useState<SignalTypeView[]>([]);
  const [loading, setLoading] = useState(true);
  const [readDenied, setReadDenied] = useState(false);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [togglingKeys, setTogglingKeys] = useState<ReadonlySet<string>>(new Set());
  const [form] = Form.useForm<FormShape>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const view = await listSignalTypes();
      setTypes(view.types);
      setReadDenied(false);
      setUnavailable(null);
    } catch (e) {
      const n = normalizeApiError(e);
      if (n.httpStatus === 403) {
        setReadDenied(true);
      } else if (n.httpStatus === 503 || n.httpStatus === 404 || n.httpStatus === 0) {
        // Honest: the catalogue lives in lifecycle-clocks; when that module (or the API) is off
        // this panel says so instead of showing an empty table that reads as "no types".
        setUnavailable(n.message);
      } else {
        message.error(n.message);
      }
      setTypes([]);
    } finally {
      setLoading(false);
    }
    // message is stable (App.useApp)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    setSubmitError(null);
    const request: SignalTypeRequest = {
      typeKey: values.typeKey.trim(),
      title: values.title.trim(),
      description: values.description?.trim() || null,
      correlationAttributes: values.correlationAttributes?.length
        ? values.correlationAttributes
        : null,
    };
    try {
      const created = await createSignalType(request);
      message.success(`Signal type ${created.typeKey} registered — it is live on the rail now.`);
      setAddOpen(false);
      form.resetFields();
      void load();
    } catch (e) {
      // The API names exactly what it refused (400 shape, 409 key taken, 503) — verbatim.
      setSubmitError(normalizeApiError(e).message);
    } finally {
      setSubmitting(false);
    }
  };

  const toggle = (type: SignalTypeView, enabled: boolean) => {
    setTogglingKeys((prev) => new Set(prev).add(type.typeKey));
    setSignalTypeEnabled(type.typeKey, enabled)
      .then((updated) => {
        setTypes((prev) => prev.map((t) => (t.typeKey === updated.typeKey ? updated : t)));
        message.success(
          enabled
            ? `${updated.typeKey} enabled — the next signed post of it lands.`
            : `${updated.typeKey} disabled — the rail refuses it (422) from the next post; history keeps.`,
        );
      })
      .catch((e) => message.error(normalizeApiError(e).message))
      .finally(() =>
        setTogglingKeys((prev) => {
          const next = new Set(prev);
          next.delete(type.typeKey);
          return next;
        }),
      );
  };

  const columns: ColumnsType<SignalTypeView> = [
    {
      title: 'Type',
      dataIndex: 'typeKey',
      key: 'typeKey',
      render: (v: string, t) => (
        <div style={{ minWidth: 180 }}>
          <Text className="ol-mono" style={{ fontSize: 12, fontWeight: 600 }} copyable>
            {v}
          </Text>
          <div style={{ fontSize: 12 }}>{t.title}</div>
          {t.description && (
            <Text type="secondary" style={{ fontSize: 11.5 }}>
              {t.description}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'Origin',
      dataIndex: 'origin',
      key: 'origin',
      width: 110,
      render: (v: string, t) =>
        v === 'PLATFORM' ? (
          <Tooltip title={t.citation ?? 'Seeded platform type — read-only here.'}>
            <Tag color="blue">PLATFORM</Tag>
          </Tooltip>
        ) : (
          <Tag>HUB</Tag>
        ),
    },
    {
      title: 'Correlates on',
      key: 'correlation',
      render: (_, t) =>
        t.correlationAttributes.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 11.5 }}>
            {t.origin === 'PLATFORM' ? 'per contract' : 'whatever hints the sender supplies'}
          </Text>
        ) : (
          <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
            {t.correlationAttributes.map((a) => (
              <Tag key={a} className="ol-mono" style={{ fontSize: 10.5, marginInlineEnd: 0 }}>
                {a}
              </Tag>
            ))}
          </span>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (v: string) => <StatusTag status={v} />,
    },
    {
      title: 'Updated',
      key: 'updated',
      width: 110,
      render: (_, t) => (
        <Text style={{ fontSize: 11.5 }} title={t.updatedAt ?? t.createdAt ?? undefined}>
          {formatRelative(t.updatedAt ?? t.createdAt)}
        </Text>
      ),
    },
    {
      title: 'Enabled',
      key: 'enabled',
      width: 90,
      render: (_, t) => {
        if (t.origin === 'PLATFORM') {
          return (
            <Tooltip title="Platform-reserved: its status is a contract fact, not hub configuration (the API refuses 422 SIGNAL_TYPE_PLATFORM_RESERVED).">
              <Text type="secondary" style={{ fontSize: 11.5 }}>
                read-only
              </Text>
            </Tooltip>
          );
        }
        const enabled = t.status === 'ACTIVE';
        return (
          <Popconfirm
            title={enabled ? `Disable ${t.typeKey}?` : `Enable ${t.typeKey}?`}
            description={
              enabled
                ? 'The rail refuses this type (422) from the next post. Signals already landed keep their rows and history — nothing is deleted.'
                : 'The next signed post of this type lands, raises its alert and follows its trigger binding.'
            }
            onConfirm={() => toggle(t, !enabled)}
            okText={enabled ? 'Disable' : 'Enable'}
            disabled={!canWrite}
          >
            <Switch
              size="small"
              checked={enabled}
              disabled={!canWrite}
              loading={togglingKeys.has(t.typeKey)}
              aria-label={`${enabled ? 'Disable' : 'Enable'} signal type ${t.typeKey}`}
            />
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <Card
      size="small"
      style={{ marginTop: 12 }}
      title={
        <span>
          <TagsOutlined style={{ marginRight: 8, color: 'var(--ol-primary)' }} aria-hidden />
          Signal types
        </span>
      }
      extra={
        canWrite ? (
          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setSubmitError(null);
              setAddOpen(true);
            }}
          >
            Add signal type
          </Button>
        ) : null
      }
    >
      <Paragraph type="secondary" style={{ fontSize: 12.5, maxWidth: 820 }}>
        The rail above admits only the types in this hub&rsquo;s catalogue. Platform types are
        seeded and read-only; register your own business events here (a screening hit, a document
        expiry, a monitoring breach) and the sending system posts <Mono>type = &lt;key&gt;</Mono>{' '}
        signed. Each landed signal raises a <Mono>SIGNAL</Mono> alert for the routing rules —{' '}
        <b>to start a flow directly from a type, wire it to the flow under the flow&rsquo;s Triggers</b>{' '}
        (a <Mono>SIGNAL</Mono> binding on the type key). Types are never deleted: disable retires
        one, and every signal it ever landed keeps its record.
      </Paragraph>

      {readDenied ? (
        <AntAlert
          type="info"
          showIcon
          message="Signal types are not visible to your role"
          description="Viewing the catalogue needs alerts.rules.read (hub admin, hub operator or auditor); registering or disabling a type needs alerts.rules.write (hub admin)."
        />
      ) : unavailable ? (
        <AntAlert
          type="warning"
          showIcon
          message="The signal catalogue is not available on this deployment"
          description={unavailable}
        />
      ) : (
        <Table<SignalTypeView>
          rowKey="typeKey"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={types}
          pagination={false}
          locale={{
            emptyText: loading
              ? ' '
              : 'No signal types yet — the catalogue is empty on this hub.',
          }}
        />
      )}

      <Modal
        open={addOpen}
        title="Add signal type"
        onCancel={() => {
          setAddOpen(false);
          setSubmitError(null);
          form.resetFields();
        }}
        onOk={() => void submit()}
        okText="Register type"
        confirmLoading={submitting}
        destroyOnClose
        width={560}
      >
        <Text type="secondary" style={{ fontSize: 12.5, display: 'block', marginBottom: 12 }}>
          The type is live on the rail the moment it is registered. Then wire it to a flow under
          the flow&rsquo;s <b>Triggers</b> — this panel only defines what may arrive.
        </Text>
        {submitError && (
          <AntAlert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message="The API refused this signal type"
            description={submitError}
          />
        )}
        <Form<FormShape> form={form} layout="vertical" initialValues={{ correlationAttributes: [] }}>
          <Form.Item
            name="typeKey"
            label="Type key"
            extra="Upper-case letters, digits and underscores, starting with a letter, at most 64 characters — the value the sender puts in `type`."
            rules={[
              { required: true, message: 'A type key is required' },
              {
                pattern: SIGNAL_TYPE_KEY_PATTERN,
                message: 'Upper-snake, letter first, ≤ 64 chars (e.g. SCREENING_HIT)',
              },
            ]}
            normalize={(v: string | undefined) => (v ?? '').toUpperCase().replace(/[\s-]+/g, '_')}
          >
            <Input placeholder="SCREENING_HIT" className="ol-mono" autoFocus maxLength={64} />
          </Form.Item>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, whitespace: true, message: 'A title is required' }]}
          >
            <Input placeholder="Screening hit from the screening engine" maxLength={200} />
          </Form.Item>
          <Form.Item name="description" label="Description (optional)">
            <Input.TextArea
              rows={2}
              maxLength={2000}
              placeholder="What this event means and who sends it — teaching copy for integrators."
            />
          </Form.Item>
          <Form.Item
            name="correlationAttributes"
            label="Correlates on (optional, advisory)"
            extra={`Entity attribute keys the sender is expected to supply as correlation_hints, strongest first (e.g. ckyc_kin, pan_ref). Up to ${MAX_CORRELATION_ATTRIBUTES}. The intake still correlates on whatever hints arrive — exact match, never a guess.`}
            rules={[
              {
                validator: (_, value: string[] | undefined) => {
                  const bad = (value ?? []).find((a) => !CORRELATION_ATTRIBUTE_PATTERN.test(a));
                  if (bad) {
                    return Promise.reject(
                      new Error(`'${bad}' is not identifier-safe (lower-case, digits, _ or ., letter first)`),
                    );
                  }
                  if ((value ?? []).length > MAX_CORRELATION_ATTRIBUTES) {
                    return Promise.reject(new Error(`At most ${MAX_CORRELATION_ATTRIBUTES} attributes`));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Select
              mode="tags"
              tokenSeparators={[',', ' ']}
              placeholder="ckyc_kin, pan_ref"
              open={false}
              className="ol-mono"
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
